import { useEffect, useState } from 'react'
import { BehaviorSubject } from 'rxjs'
import { fetchWrapper } from './apiHandlers'

// Where the shopper is. Starts from an IP lookup on the server; the person can override it
// with the location picker, and that choice is remembered in this browser.
export interface ShopLocation {
  city: string
  state: string
  lat: number
  lon: number
  source: 'ip' | 'chosen'
}

const KEY = 'miamigo_shop_location'

const readChosen = (): ShopLocation | null => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || 'null')
    return v && typeof v.lat === 'number' && typeof v.lon === 'number' ? { ...v, source: 'chosen' } : null
  } catch {
    return null
  }
}

const subject = new BehaviorSubject<ShopLocation | null>(readChosen())
let detecting = false
let detected = false

const detect = async () => {
  if (subject.value || detecting || detected) return
  detecting = true
  try {
    const r: any = await fetchWrapper.get('/api/directory/whereami')
    if (r && r.location && !subject.value) subject.next({ ...r.location, source: 'ip' })
  } catch (e) {
    // no location is fine: the server then falls back to its own IP lookup
  }
  detecting = false
  detected = true
}

export const shopLocation = {
  get value() {
    return subject.value
  },
  async choose(query: string): Promise<string> {
    const r: any = await fetchWrapper.get(`/api/directory/locate?q=${encodeURIComponent(query)}`)
    if (r.status !== 200) return r.msg || 'Could not find that place.'
    const loc: ShopLocation = { ...r.location, source: 'chosen' }
    try { localStorage.setItem(KEY, JSON.stringify(loc)) } catch (e) {}
    subject.next(loc)
    return ''
  },
  async useMyLocation() {
    try { localStorage.removeItem(KEY) } catch (e) {}
    subject.next(null)
    detected = false
    await detect()
  },
}

export const useShopLocation = () => {
  const [location, setlocation] = useState<ShopLocation | null>(subject.value)
  const [ready, setready] = useState(!!subject.value || detected)
  useEffect(() => {
    const sub = subject.subscribe((l) => setlocation(l))
    detect().then(() => setready(true))
    return () => sub.unsubscribe()
  }, [])
  return { location, ready }
}

export const milesLabel = (km?: number) => (typeof km === 'number' ? `${(km * 0.621371).toFixed(km < 16 ? 1 : 0)} mi` : '')
